import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, User, Tag, Phone } from 'lucide-react';
import { blogPosts } from '../data/blogData';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="blog-post-not-found">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-black mb-4">Article Not Found</h1>
          <Button onClick={() => navigate('/blog')} variant="outline">Back to Journal</Button>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    return content.split('\n').map((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('## ')) return <h2 key={idx} className="text-2xl font-bold text-black mt-10 mb-4">{trimmed.replace('## ', '')}</h2>;
      if (trimmed.startsWith('### ')) return <h3 key={idx} className="text-xl font-bold text-black mt-8 mb-3">{trimmed.replace('### ', '')}</h3>;
      if (trimmed.startsWith('> ')) return <blockquote key={idx} className="border-l-4 border-red-600 pl-4 my-6 italic text-gray-700">{trimmed.replace('> ', '').replace(/"/g, '')}</blockquote>;
      if (trimmed.startsWith('- ')) return <li key={idx} className="ml-6 list-disc text-gray-700 leading-relaxed mb-1">{trimmed.replace('- ', '')}</li>;

      // Handle bold text
      const parts = trimmed.split(/(\*\*.*?\*\*)/g);
      const rendered = parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="text-black">{part.slice(2, -2)}</strong>;
        }
        // Handle italic
        const italicParts = part.split(/(\*.*?\*)/g);
        return italicParts.map((ip, j) => {
          if (ip.startsWith('*') && ip.endsWith('*') && !ip.startsWith('**')) {
            return <em key={`${i}-${j}`}>{ip.slice(1, -1)}</em>;
          }
          return ip;
        });
      });

      return <p key={idx} className="text-gray-700 leading-relaxed mb-4">{rendered}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      {/* Header */}
      <div className="bg-black text-white py-20">
        <div className="max-w-3xl mx-auto px-6">
          <button onClick={() => navigate('/blog')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Journal
          </button>
          <span className="text-xs font-bold text-red-500 tracking-widest">{post.category.toUpperCase()}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-6 leading-tight" data-testid="blog-post-title">{post.title}</h1>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.readTime}</span>
            <span>{post.date}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-16">
        <article className="prose-lg" data-testid="blog-post-content">
          {renderContent(post.content)}
        </article>

        {/* Tags */}
        <div className="flex items-center gap-2 mt-12 pt-8 border-t border-gray-200">
          <Tag className="h-4 w-4 text-gray-400" />
          {post.tags.map((tag, idx) => (
            <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">{tag}</span>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-black mb-3">Ready to experience the ACE standard?</h3>
          <p className="text-gray-600 mb-6">Book a free Discovery Call or reserve your vehicle today.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button onClick={() => navigate('/booking')} className="bg-red-600 hover:bg-red-700 text-white">Book Now</Button>
            <Button onClick={() => navigate('/contact')} variant="outline" className="border-gray-300 hover:bg-black hover:text-white">Discovery Call</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
